import mousetrap from "mousetrap";
import { a, getSelectedShapes, setMode } from "../../../shared/globalState/a";
import { Shape } from "../../../shared/types";
import { Block } from "../../../models/shapes/Block";
import { Line } from "../../../models/shapes/Line";

/**
 * Input handlers and bindings for ObjectProperties component
 */

export interface InputState {
  isRound: boolean;
  isRect: boolean;
  diameter: number;
  width: number;
  height: number;
  flowRate: number;
  calledWithShortcut: boolean;
  focusFlowRateInput: boolean;
  show: boolean;
}

/**
 * Handle radio button change for shape type (round/rect)
 */
export function handleRadioChange(event: Event, state: InputState): Partial<InputState> {
  const name = (event.target as HTMLInputElement).name;
  if (name === "round") {
    return { isRound: true, isRect: false };
  } else if (name === "rect") {
    return { isRound: false, isRect: true };
  }
  return {};
}

/**
 * Handle diameter input change
 */
export function handleDiameterInput(
  event: Event,
  diameter: number,
  selectedSegments: Line[],
  setHasPendingChanges: () => void
): void {
  a.preNetInfo.diameterMil = diameter;

  if (selectedSegments.length) {
    selectedSegments.forEach((s) => {
      s.net.diameterMil = diameter;
    });
  }

  setHasPendingChanges();
}

/**
 * Handle width input change
 */
export function handleWidthInput(
  event: Event,
  width: number,
  selectedSegments: Line[],
  setHasPendingChanges: () => void
): void {
  a.preNetInfo.widthMil = width;

  if (selectedSegments.length) {
    selectedSegments.forEach((s) => {
      s.net.widthMil = width;
    });
  }

  setHasPendingChanges();
}

/**
 * Handle height input change
 */
export function handleHeightInput(
  event: Event,
  height: number,
  selectedSegments: Line[],
  setHasPendingChanges: () => void
): void {
  a.preNetInfo.heightMil = height;

  if (selectedSegments.length) {
    selectedSegments.forEach((s) => {
      s.net.heightMil = height;
    });
  }

  setHasPendingChanges();
}

/**
 * Handle flow rate input change
 */
export function handleFlowRateChange(
  event: Event,
  setFlowRate: (rate: number) => void,
  setHasPendingChanges: () => void
): void {
  const target = event.target as HTMLInputElement;
  const flowRate = target.valueAsNumber;
  setFlowRate(flowRate);
  a.preNetInfo.flowRateCmh = flowRate;
  setHasPendingChanges();
}

/**
 * Handle escape key press to close dialog
 */
export function handleEscapePress(
  event: KeyboardEvent,
  setShow: (show: boolean) => void,
  setCalledWithShortcut: (called: boolean) => void,
  setFocusFlowRateInput: (focus: boolean) => void
): void {
  const element = event.target as HTMLElement;
  element.blur();
  setMode("select");

  setShow(false);
  setCalledWithShortcut(false);
  setFocusFlowRateInput(false);
  a.commandsUI.callNetworkInfoWindow$.next({
    show: false,
    calledWithShortcut: false,
  });
}

/**
 * Focus handler for input fields (called via shortcut)
 */
export function createFocusHandler(calledWithShortcut: boolean, focusFlowRateInput: boolean) {
  return function focus(node: HTMLInputElement): void {
    if (calledWithShortcut || focusFlowRateInput) {
      node.focus();
      node.select();
    }
  };
}

/**
 * Focus handler specifically for flow rate input
 */
export function createFocusFlowRateHandler(focusFlowRateInput: boolean) {
  return function focusFlowRate(node: HTMLInputElement): void {
    if (focusFlowRateInput) {
      node.focus();
      node.select();
    }
  };
}

/**
 * Bind escape key handler to input field
 */
export function createEscapeBinding(handleEscapePressFn: (e: KeyboardEvent) => void) {
  return function bindEscapeInInputFields(node: HTMLInputElement): void {
    mousetrap(node).bind("escape", handleEscapePressFn);
  };
}

/**
 * Bind tab key handler to prevent default tab behavior
 */
export function createTabBinding() {
  return function bindTabInInputFields(node: HTMLElement) {
    mousetrap(node).bind("tab", function (e: KeyboardEvent) {
      e.preventDefault();
      node.blur();
    });
  };
}

/**
 * Bind enter key handler to trigger calculations
 */
export function createEnterBinding(
  assignNetParamsFn: () => void,
  updateCalculationsFn: () => void,
  setHasPendingChanges: (pending: boolean) => void,
  resetInputsFn: () => void
) {
  return function bindEnterInInputFields(node: HTMLElement) {
    mousetrap(node).bind("enter", function (e: KeyboardEvent) {
      e.preventDefault();

      // Assign user-input values to network elements before calculations
      assignNetParamsFn();

      // Update calculations
      updateCalculationsFn();

      // Clear pending changes flag
      setHasPendingChanges(false);

      // Reset values in flowRate input, diameter input, width and height inputs
      resetInputsFn();
    });
  };
}

/**
 * Reset all input field values
 */
export function resetInputs(
  setFlowRate: (rate: number) => void,
  setDiameter: (diameter: number) => void,
  setWidth: (width: number) => void,
  setHeight: (height: number) => void
): void {
  // Reset bound variables (Svelte will update the DOM automatically)
  setFlowRate(0);
  setDiameter(0);
  setWidth(0);
  setHeight(0);

  // Reset input field DOM elements to ensure they're visually cleared
  const flowRateInput = document.getElementById("flow-rate") as HTMLInputElement;
  if (flowRateInput) {
    flowRateInput.value = "";
  }

  const diameterInput = document.getElementById("round-diameter") as HTMLInputElement;
  if (diameterInput) {
    diameterInput.value = "";
  }

  const widthInput = document.getElementById("rect-width") as HTMLInputElement;
  if (widthInput) {
    widthInput.value = "";
  }

  const heightInput = document.getElementById("rect-height") as HTMLInputElement;
  if (heightInput) {
    heightInput.value = "";
  }
}

/**
 * Select all text content in an input field
 */
export function selectTextContent(event: Event): void {
  (event.target as HTMLInputElement).select();
}

